import { metersToFeet } from './format';

// Length filter unit conversion for the market/comp-set wizard.
//
// 1 ft = 0.3048 m exactly. The filter is entered in whole feet (charterers
// think in feet) but persisted in meters to keep the API/schema unchanged.
//
// Inclusivity model (issue #154): a boat DISPLAYS as N ft iff
// metersToFeet(lengthM) === N. A min/max of N ft should match exactly the boats
// shown as N ft, so the inclusive SQL `>=` / `<=` bound is the actual smallest /
// largest 2-decimal lengthM that still displays as N ft. We derive those from
// metersToFeet itself (walking out from the analytic edge), so the bounds always
// agree with what's rendered — including round()'s float quirks at the half-foot
// boundaries (e.g. 34.29 m shows as 112 ft, not 113). Rounding the raw edge
// to-nearest instead leaked one 2dp step into the neighbour foot — a max=3 ft
// filter matched a boat shown as 4 ft.
export const M_PER_FT = 0.3048;

/** Whole-ft minimum -> smallest 2dp lengthM still shown as >= N ft (inclusive). */
export const ftToMinM = (ft: number): number => {
  let cents = Math.round((ft - 0.5) * M_PER_FT * 100);
  while (metersToFeet((cents - 1) / 100) >= ft) cents--;
  while (metersToFeet(cents / 100) < ft) cents++;
  return cents / 100;
};

/** Whole-ft maximum -> largest 2dp lengthM still shown as <= N ft (inclusive). */
export const ftToMaxM = (ft: number): number => {
  let cents = Math.round((ft + 0.5) * M_PER_FT * 100);
  while (metersToFeet((cents + 1) / 100) <= ft) cents++;
  while (metersToFeet(cents / 100) > ft) cents--;
  return cents / 100;
};

// Inverses of the two functions above — used to render a stored metric bound
// back as the whole-ft value the user entered. They MUST mirror the ∓0.5-ft
// edge offset. Using a plain round-to-nearest (`metersToFeet`) inverts the bin
// CENTRE rather than its edge, which shifted the displayed value by a foot:
// typing "3" rendered "2" in the From field and "4" in the To field, leaving
// the controlled input unusable.

// `Math.round` yields -0 for inputs just below zero (e.g. the 0-ft max edge);
// normalise to +0 so 0 ft round-trips cleanly and never renders as "-0".
const noNegZero = (n: number): number => (n === 0 ? 0 : n);

/** Stored metric lower bound -> the whole-ft minimum the user entered. */
export const minMToFt = (m: number): number => noNegZero(Math.round(m / M_PER_FT + 0.5));

/** Stored metric upper bound -> the whole-ft maximum the user entered. */
export const maxMToFt = (m: number): number => noNegZero(Math.round(m / M_PER_FT - 0.5));
